import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { OverlayGrayBackground } from '../../components/OverlayGrayBackground';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import ProductSelectorModal from '../../components/ProductSelectorModal';
import { ConfrimModal } from '../../components/ConfrimModal';
import FbaTransferStatusChip from './FbaTransferStatusChip';
import { apiError, formatDateTime, formatMoney, formatQty } from './format';

const EDITABLE = ['DRAFT'];
const RUNNABLE = ['DRAFT', 'EXCEPTION'];
const ATTACHABLE = ['AWAITING_AMAZON_SHIPMENT', 'AMAZON_INBOUND'];
const REMOVAL_LABEL = { PENDING: 'Not removed', SUBMITTING: 'Unknown', VERIFIED: 'Verified', UNVERIFIED: 'Unverified', FAILED: 'Failed', CLOSED: 'Closed' };
const REMOVAL_CLASS = { VERIFIED: 'text-green-700', UNVERIFIED: 'text-red-700', FAILED: 'text-red-700', SUBMITTING: 'text-amber-700', CLOSED: 'text-gray-500' };
const SETTLED = ['VERIFIED', 'CLOSED'];
const UNKNOWN = ['SUBMITTING', 'UNVERIFIED'];
const inputClass = 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2';

const lineKey = () => `new-${Math.random().toString(36).slice(2)}`;

const FbaTransferSidebar = ({ isOpen, transferId, canEdit, onClose, onChanged }) => {
  const [transfer, setTransfer] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState('');
  const [stock, setStock] = useState({});
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [shipmentId, setShipmentId] = useState('');
  const [groups, setGroups] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState('');

  const stockRequests = useRef(new Map());
  // Own signal per SKU: without one, the global axios interceptor aborts every earlier source-stock GET when the next SKU's starts.
  const loadStock = useCallback(async (sku) => {
    if (!sku) return;
    stockRequests.current.get(sku)?.abort();
    const controller = new AbortController();
    stockRequests.current.set(sku, controller);
    try {
      const { data } = await axios.get('/api/v1/warehouse/fba-transfers/source-stock', { params: { sku }, signal: controller.signal });
      setStock((current) => ({ ...current, [sku]: data }));
    } catch (error) {
      if (axios.isCancel(error)) return;
      setStock((current) => ({ ...current, [sku]: { rows: [], error: true } }));
    }
  }, []);

  const applyTransfer = useCallback((data) => {
    setTransfer(data);
    setNotes(data?.notes || '');
    setShipmentId(data?.amazon_shipment_id || '');
    setLines((data?.lines || []).map((line) => ({ ...line, key: String(line.id), sku: line.supply_sku, location: line.source_location_code, quantity: line.qty_planned })));
    setDirty(false);
    if (data && RUNNABLE.includes(data.status)) [...new Set(data.lines.map((line) => line.supply_sku))].forEach(loadStock);
  }, [loadStock]);

  useEffect(() => {
    if (!isOpen) return;
    setStock({});
    setGroups(null);
    if (!transferId) {
      applyTransfer(null);
      return;
    }
    setLoading(true);
    axios.get(`/api/v1/warehouse/fba-transfers/${transferId}`)
      .then(({ data }) => applyTransfer(data))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        toast.error(apiError(error, 'Failed to load transfer'));
      })
      .finally(() => setLoading(false));
  }, [isOpen, transferId, applyTransfer]);

  const status = transfer?.status || 'DRAFT';
  const editable = canEdit && EDITABLE.includes(status);

  const lineIssues = useMemo(() => {
    const seen = new Map();
    lines.forEach((line) => {
      const key = `${line.sku}|${line.location}`;
      seen.set(key, (seen.get(key) || 0) + 1);
    });
    return new Map(lines.map((line) => {
      const issues = [];
      const qty = Number(line.quantity);
      const option = (stock[line.sku]?.rows || []).find((row) => row.location_code === line.location);
      if (!line.location) issues.push('Pick a source location');
      if (!Number.isInteger(qty) || qty <= 0) issues.push('Quantity must be a whole number above 0');
      if (line.location && stock[line.sku] && !option) issues.push('No stock for this SKU at that location');
      if (option && !option.usable) issues.push(option.reason);
      if (option && qty > option.quantity) issues.push(`Only ${option.quantity} available`);
      if (seen.get(`${line.sku}|${line.location}`) > 1) issues.push('Same SKU and location listed twice');
      return [line.key, issues];
    }));
  }, [lines, stock]);

  // Retries are revalidated by the server; verified lines no longer match live stock.
  const hasIssues = editable && [...lineIssues.values()].some((issues) => issues.length);

  const updateLine = (key, changes) => {
    setDirty(true);
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...changes } : line)));
  };

  useEffect(() => {
    if (!isOpen || !editable || groups) return undefined;
    let active = true;
    setGroupsLoading(true);
    axios.get('/api/v1/warehouse/fba-transfers/products')
      .then(({ data }) => active && setGroups(data.groups || []))
      .catch((error) => active && !axios.isCancel(error) && toast.error(apiError(error, 'Failed to load products')))
      .finally(() => active && setGroupsLoading(false));
    return () => { active = false; };
  }, [isOpen, editable, groups]);

  const selectedProductIds = useMemo(() => {
    const skus = new Set(lines.map((line) => line.sku));
    return (groups || []).flatMap((group) => group.variants).filter((variant) => skus.has(variant.sku)).map((variant) => variant.id);
  }, [groups, lines]);

  const addProducts = (items) => {
    const existing = new Set(lines.map((line) => line.sku));
    const added = items.filter((item) => !existing.has(item.sku));
    if (!added.length) return;
    setDirty(true);
    setLines((current) => [...current, ...added.map((item) => ({ key: lineKey(), sku: item.sku, product_name: item.name, location: '', quantity: '' }))]);
    added.forEach((item) => loadStock(item.sku));
  };

  const run = async (label, request, success) => {
    setBusy(label);
    try {
      const { data } = await request();
      applyTransfer(data);
      onChanged?.();
      if (success) toast.success(success(data));
      return data;
    } catch (error) {
      toast.error(apiError(error, `${label} failed`));
      if (transfer?.id) {
        axios.get(`/api/v1/warehouse/fba-transfers/${transfer.id}`).then(({ data }) => applyTransfer(data)).catch(() => {});
        onChanged?.();
      }
      return null;
    } finally {
      setBusy(null);
    }
  };

  const payload = () => ({ notes, lines: lines.map((line) => ({ sku: line.sku, location: line.location, quantity: Number(line.quantity), amazon_seller_sku: line.amazon_seller_sku || undefined })) });
  const saveDraft = () => run('Save', () => (transfer?.id
    ? axios.put(`/api/v1/warehouse/fba-transfers/${transfer.id}`, payload())
    : axios.post('/api/v1/warehouse/fba-transfers', payload())), (data) => (transfer?.id ? `${data.transfer_number} saved` : `${data.transfer_number} created as a Draft`));

  const execute = () => run('Publish', () => axios.post(`/api/v1/warehouse/fba-transfers/${transfer.id}/execute`), (data) => (
    data.status === 'AWAITING_AMAZON_SHIPMENT' ? `${data.transfer_number} published: removed from ShipHero and verified` : `${data.transfer_number} needs attention`
  ));
  const cancel = () => run('Cancel', () => axios.post(`/api/v1/warehouse/fba-transfers/${transfer.id}/cancel`, { reason: reason.trim() }), (data) => `${data.transfer_number} cancelled`);
  const closeLines = () => run('Close lines', () => axios.post(`/api/v1/warehouse/fba-transfers/${transfer.id}/close-remaining-lines`, { reason: reason.trim() }), (data) => `${data.transfer_number}: remaining lines closed`);
  const resolve = (decision) => run(decision === 'STILL_IN_WAREHOUSE' ? 'Reverse' : 'Recovery', () => axios.post(`/api/v1/warehouse/fba-transfers/${transfer.id}/resolve-cancellation`, { decision, reason: reason.trim() }), (data) => (
    data.status === 'CANCELLED_REVERSED' ? `${data.transfer_number}: units added back to ShipHero and verified`
      : data.status === 'CANCELLED_AFTER_DISPATCH' ? `${data.transfer_number} marked Cancelled After Dispatch / Needs Recovery` : `${data.transfer_number} needs attention`
  ));
  const openModal = (name) => { setReason(''); setModal(name); };
  const confirm = async (action) => { await action(); setModal(null); };

  const attach = () => run('Attach shipment', () => axios.post(`/api/v1/warehouse/fba-transfers/${transfer.id}/amazon-shipment`, { amazonShipmentId: shipmentId }), (data) => `${data.amazon_shipment_id} attached`);

  if (!isOpen) return null;

  const nothingRemoved = (transfer?.lines || []).every((line) => ['PENDING', 'FAILED'].includes(line.removal_state));
  const canAttach = canEdit && ATTACHABLE.includes(status) && !transfer?.amazon_last_synced_at;
  const canClose = canEdit && status === 'EXCEPTION'
    && (transfer?.lines || []).some((line) => line.removal_state === 'VERIFIED')
    && !(transfer?.lines || []).some((line) => UNKNOWN.includes(line.removal_state));
  const publishLines = lines.filter((line) => !SETTLED.includes(line.removal_state)).map((line) => {
    const option = (stock[line.sku]?.rows || []).find((row) => row.location_code === line.location);
    const qty = Number(line.quantity || 0);
    const unitCost = line.unit_cost ?? line.unit_cost_preview ?? stock[line.sku]?.unit_cost;
    return { ...line, qty, current: option?.quantity ?? null, unitCost };
  });
  const publishUnits = publishLines.reduce((sum, line) => sum + line.qty, 0);
  const publishValue = publishLines.every((line) => line.unitCost != null)
    ? publishLines.reduce((sum, line) => sum + Number(line.unitCost) * line.qty, 0)
    : null;
  const reasonModal = modal === 'cancel' || modal === 'close';
  const title = transfer ? `Transfer ${transfer.transfer_number}` : 'New Amazon FBA transfer';

  return (
    <div className="fixed right-0 top-0 z-50 flex h-full w-[960px] max-w-full flex-col border-l bg-white shadow-lg max-[639px]:top-14 max-[639px]:h-[calc(100dvh-3.5rem)]">
      <div className="flex items-start justify-between border-b p-6">
        <div>
          <h1 className="text-xl font-bold">{title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <FbaTransferStatusChip status={status} />
            <span>Destination: Amazon FBA (In Transit)</span>
            {transfer?.requested_by && <span>Requested by {transfer.requested_by}</span>}
            {transfer?.executed_by && <span>Executed by {transfer.executed_by} on {formatDateTime(transfer.executed_at)}</span>}
          </div>
        </div>
        <button className="h-8 w-8" onClick={onClose} type="button" aria-label="Close"><X className="h-5 w-5" /></button>
      </div>
      <div className={`relative flex-1 ${busy ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        {busy && <OverlayGrayBackground additionalClass="!fixed" />}
        {loading ? (
          <div className="flex h-full items-center justify-center"><LoaderSpinner width="70" height="70" border="3" colorSpinner="black" /></div>
        ) : (
          <div className="space-y-6 p-6">
            {transfer?.lines_closed_reason && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
                <span className="font-medium">Remaining lines closed</span> by {transfer.lines_closed_by} on {formatDateTime(transfer.lines_closed_at)}: {transfer.lines_closed_reason}
              </div>
            )}
            {!!transfer?.exceptions?.length && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                <div className="mb-1 flex items-center gap-1 font-medium"><ExclamationTriangleIcon className="h-4 w-4" /> Exceptions</div>
                <ul className="list-disc pl-5">{transfer.exceptions.map((item, index) => <li key={`${item.code}-${index}`}>{item.sku ? `${item.sku}: ` : ''}{item.message}</li>)}</ul>
              </div>
            )}

            <div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">Lines</h3>
              {editable && (
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-600">Add every SKU going to Amazon, then pick a source location and quantity for each line.</p>
                  <button type="button" onClick={() => setPickerOpen(true)} disabled={groupsLoading} className="relative inline-flex shrink-0 items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-wait">
                    {groupsLoading && <span className="absolute inset-0 flex items-center justify-center"><LoaderSpinner width="18" height="18" border="2" colorSpinner="black" /></span>}
                    <span className={groupsLoading ? 'opacity-0' : ''}>Add Products</span>
                  </button>
                </div>
              )}
              <ProductSelectorModal
                isOpen={pickerOpen}
                onClose={() => setPickerOpen(false)}
                selectedIds={selectedProductIds}
                groups={groups || []}
                loading={groupsLoading}
                subtitle="Select products for this Amazon transfer"
                showUnitCost={false}
                onConfirm={addProducts}
              />
              <div className="overflow-x-auto rounded-lg border">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left">
                    {editable ? (
                      <tr><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Source location</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Unit cost</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2" /></tr>
                    ) : (
                      <tr><th className="px-3 py-2">SKU</th><th className="px-3 py-2">From</th><th className="px-3 py-2 text-right">Planned</th><th className="px-3 py-2 text-right">Removed</th><th className="px-3 py-2">ShipHero before → after</th><th className="px-3 py-2">Removal</th><th className="px-3 py-2 text-right">Unit cost</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2 text-right">Amazon shipped</th><th className="px-3 py-2 text-right">Amazon received</th></tr>
                    )}
                  </thead>
                  <tbody>
                    {!lines.length && <tr><td colSpan={10} className="px-3 py-6 text-center text-gray-500">Click Add Products to pick the SKUs going to Amazon.</td></tr>}
                    {lines.map((line) => {
                      const skuStock = stock[line.sku];
                      const issues = lineIssues.get(line.key) || [];
                      const unitCost = line.unit_cost ?? line.unit_cost_preview ?? (editable ? skuStock?.unit_cost : null);
                      const value = unitCost == null || !Number(line.quantity) ? null : Number(unitCost) * Number(line.quantity);
                      if (editable) {
                        return (
                          <tr key={line.key} className="border-t align-top">
                            <td className="px-3 py-2"><div className="font-medium">{line.sku}</div><div className="text-xs text-gray-500">{line.product_name || skuStock?.product_name}</div></td>
                            <td className="px-3 py-2">
                              <select value={line.location} onChange={(event) => updateLine(line.key, { location: event.target.value })} className={`${inputClass} min-w-[240px]`}>
                                <option value="">{skuStock ? (skuStock.rows.length ? 'Pick location' : 'No ShipHero stock') : 'Loading…'}</option>
                                {(skuStock?.rows || []).map((row) => (
                                  <option key={row.location_code} value={row.location_code} disabled={!row.usable}>{row.location_code} ({row.quantity} available{row.usable ? '' : ', blocked'})</option>
                                ))}
                              </select>
                              {issues.map((issue) => <p key={issue} className="mt-1 text-xs text-red-600">{issue}</p>)}
                            </td>
                            <td className="px-3 py-2"><input type="number" min="1" step="1" value={line.quantity} onChange={(event) => updateLine(line.key, { quantity: event.target.value })} className={`${inputClass} no-arrows-by-input w-24 text-right`} /></td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatMoney(unitCost)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatMoney(value)}</td>
                            <td className="px-3 py-2"><button type="button" aria-label="Remove line" onClick={() => { setDirty(true); setLines((current) => current.filter((l) => l.key !== line.key)); }}><TrashIcon className="h-5 w-5 text-gray-500" /></button></td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={line.key} className={`border-t ${line.exception_reason ? 'bg-red-50' : ''}`}>
                          <td className="px-3 py-2"><div className="font-medium">{line.supply_sku}</div><div className="text-xs text-gray-500">{line.product_name}{line.amazon_seller_sku ? ` · Amazon SKU ${line.amazon_seller_sku}` : ''}</div></td>
                          <td className="px-3 py-2">{line.source_location_code}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatQty(line.qty_planned)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatQty(line.qty_removed)}</td>
                          <td className="px-3 py-2 tabular-nums">{line.shiphero_before == null ? '—' : `${line.shiphero_before} → ${line.shiphero_after ?? '?'}`}</td>
                          <td className={`px-3 py-2 ${REMOVAL_CLASS[line.removal_state] || ''}`} title={line.exception_reason || ''}>{REMOVAL_LABEL[line.removal_state] || line.removal_state}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatMoney(unitCost)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatMoney(line.extended_value ?? line.extended_value_preview)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatQty(line.amazon_qty_shipped)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatQty(line.amazon_qty_received)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {transfer?.totals && <p className="mt-2 text-right text-sm text-gray-700">{formatQty(transfer.totals.qty_planned)} planned · {formatQty(transfer.totals.qty_removed)} removed · {formatMoney(transfer.totals.extended_value)} value</p>}
            </div>

            {transfer && !['DRAFT', 'CANCELLED'].includes(status) && (
              <div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">Amazon shipment</h3>
                <p className="mb-2 text-sm text-gray-600">Create the inbound shipment and labels in Seller Central, then enter its shipment ID here.</p>
                <div className="flex gap-2">
                  <input value={shipmentId} onChange={(event) => setShipmentId(event.target.value)} disabled={!canAttach} placeholder="FBA15ABC1234" className={`${inputClass} max-w-xs uppercase`} />
                  {canAttach && <button type="button" onClick={attach} disabled={!shipmentId.trim() || !!busy} className="rounded-lg bg-black px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{transfer.amazon_shipment_id ? 'Update ID' : 'Attach ID'}</button>}
                </div>
                {transfer.amazon_shipment_id && (
                  <p className="mt-2 text-sm text-gray-600">
                    Attached by {transfer.amazon_shipment_attached_by} on {formatDateTime(transfer.amazon_shipment_attached_at)}. Amazon sync: {transfer.amazon_last_synced_at ? `last synced ${formatDateTime(transfer.amazon_last_synced_at)}` : 'not available yet. Eadin reads Amazon status and received quantities once the Fulfillment role is enabled.'}
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="fba-transfer-notes" className="mb-2 block text-lg font-medium text-gray-900">Notes</label>
              <textarea id="fba-transfer-notes" rows={3} value={notes} disabled={!editable} onChange={(event) => { setDirty(true); setNotes(event.target.value); }} className={inputClass} />
            </div>

            {!!transfer?.audit_trail?.length && (
              <div>
                <h3 className="mb-2 text-lg font-medium text-gray-900">Timeline</h3>
                <ol className="space-y-2 border-l pl-4 text-sm">
                  {transfer.audit_trail.map((entry) => (
                    <li key={entry.id}><span className="text-gray-500">{formatDateTime(entry.created_at)}</span> · {entry.summary} <span className="text-gray-500">({entry.changed_by})</span></li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t p-4">
          {transfer && RUNNABLE.includes(status) && nothingRemoved && (
            <button type="button" onClick={() => openModal('cancel')} disabled={!!busy} className="mr-auto inline-flex h-8 items-center rounded-md border border-red-200 bg-white px-3 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50">Cancel Transfer</button>
          )}
          {transfer && status === 'CANCELLATION_EXCEPTION' && (
            <>
              <button type="button" onClick={() => openModal('recovery')} disabled={!!busy} className="mr-auto inline-flex h-8 items-center rounded-md border border-orange-200 bg-orange-50 px-3 text-sm text-orange-900 hover:bg-orange-100 disabled:opacity-50">Already Left Warehouse</button>
              <button type="button" onClick={() => openModal('reverse')} disabled={!!busy} className="inline-flex h-8 items-center rounded-md border border-green-200 bg-green-50 px-3 text-sm text-green-900 hover:bg-green-100 disabled:opacity-50">Still in Warehouse: Add Back</button>
            </>
          )}
          {canClose && (
            <button type="button" onClick={() => openModal('close')} disabled={!!busy} className="mr-auto inline-flex h-8 items-center rounded-md border border-amber-200 bg-amber-50 px-3 text-sm text-amber-900 hover:bg-amber-100 disabled:opacity-50">Close Remaining Lines</button>
          )}
          {editable && (
            <button type="button" onClick={saveDraft} disabled={!lines.length || hasIssues || !!busy || (transfer && !dirty)} className="inline-flex h-8 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {busy === 'Save' ? 'Saving…' : transfer ? 'Save Changes' : 'Create Transfer'}
            </button>
          )}
          {transfer && RUNNABLE.includes(status) && (
            <button type="button" onClick={() => openModal('publish')} disabled={dirty || hasIssues || !!busy} title={dirty ? 'Save your changes first' : ''} className="inline-flex h-8 items-center rounded-md border border-green-200 bg-green-50 px-3 text-sm text-green-900 hover:bg-green-100 disabled:opacity-50">
              {busy === 'Publish' ? 'Publishing…' : status === 'EXCEPTION' ? 'Retry Publish' : 'Publish Transfer'}
            </button>
          )}
        </div>
      )}
      <ConfrimModal
        isOpen={modal === 'publish'}
        isLoading={busy === 'Publish'}
        title={status === 'EXCEPTION' ? `Retry Publish ${transfer?.transfer_number || ''}` : 'Publish Transfer'}
        description={status === 'EXCEPTION'
          ? 'Only lines that are not Verified are retried, so nothing is removed twice. Eadin checks ShipHero stock again, removes the units, then reads ShipHero back to verify each line.'
          : 'Once this transfer is published, these units are removed from ShipHero now. Eadin checks stock again first, then reads ShipHero back to verify each line. Do you wish to continue?'}
        cancelButtonTitle="No"
        confirmButtonTitle="Yes, Publish"
        cancelClick={() => setModal(null)}
        submitClick={() => confirm(execute)}
        size="max-w-2xl"
      >
        <div className="mb-5 overflow-hidden rounded-md border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr><th className="px-3 py-2">SKU</th><th className="px-3 py-2">Location</th><th className="px-3 py-2 text-right">ShipHero now</th><th className="px-3 py-2 text-right">Remove</th><th className="px-3 py-2 text-right">After</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {publishLines.map((line) => (
                <tr key={line.key}>
                  <td className="px-3 py-2 font-medium">{line.sku}</td>
                  <td className="px-3 py-2">{line.location}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatQty(line.current)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">-{formatQty(line.qty)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{line.current == null ? '—' : formatQty(line.current - line.qty)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t bg-gray-50 px-3 py-2 text-right text-sm text-gray-700">{formatQty(publishUnits)} units · {formatMoney(publishValue)} move to Amazon In Transit</p>
        </div>
      </ConfrimModal>
      <ConfrimModal
        isOpen={modal === 'reverse' || modal === 'recovery'}
        isLoading={busy === 'Reverse' || busy === 'Recovery'}
        title={modal === 'reverse' ? `Add ${transfer?.transfer_number || 'transfer'} back to ShipHero?` : `Mark ${transfer?.transfer_number || 'transfer'} as left the warehouse?`}
        description={modal === 'reverse'
          ? 'Amazon cancelled this shipment and the units are still in the warehouse. Eadin adds exactly what it removed back to the same bin(s), then reads ShipHero back to verify each line. The original removal stays in the history.'
          : 'Amazon cancelled this shipment but the units already left the warehouse. ShipHero is not changed. The transfer becomes Cancelled After Dispatch / Needs Recovery and stays on the ledger for Amazon recovery.'}
        cancelButtonTitle="No"
        confirmButtonTitle={modal === 'reverse' ? 'Yes, Add Back' : 'Yes, Needs Recovery'}
        cancelClick={() => setModal(null)}
        submitClick={() => confirm(() => resolve(modal === 'reverse' ? 'STILL_IN_WAREHOUSE' : 'LEFT_WAREHOUSE'))}
        size="max-w-lg"
      >
        <div className="mb-5">
          <label htmlFor="fba-cancellation-note" className="mb-1 block text-sm font-medium text-gray-900">Note (optional)</label>
          <textarea id="fba-cancellation-note" rows={2} value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
        </div>
      </ConfrimModal>
      <ConfrimModal
        isOpen={reasonModal}
        isLoading={busy === 'Cancel' || busy === 'Close lines'}
        title={modal === 'cancel' ? `Cancel ${transfer?.transfer_number || 'transfer'}?` : 'Close Remaining Lines'}
        description={modal === 'cancel'
          ? 'Nothing has left ShipHero, so cancelling changes no stock. The transfer record and its history are kept.'
          : 'Verified lines stay in transit to Amazon. Failed or not-yet-removed lines are closed without removing anything from ShipHero. The reason is saved in the timeline and on the Finance report.'}
        cancelButtonTitle="No"
        confirmButtonTitle={modal === 'cancel' ? 'Yes, Cancel Transfer' : 'Yes, Close Lines'}
        cancelClick={() => setModal(null)}
        submitClick={() => reason.trim() && confirm(modal === 'cancel' ? cancel : closeLines)}
        size="max-w-lg"
      >
        <div className="mb-5">
          <label htmlFor="fba-transfer-reason" className="mb-1 block text-sm font-medium text-gray-900">Reason (required)</label>
          <textarea id="fba-transfer-reason" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          {!reason.trim() && <p className="mt-1 text-xs text-red-600">Enter a reason to continue.</p>}
        </div>
      </ConfrimModal>
    </div>
  );
};

export default FbaTransferSidebar;
